/**
 * Visual shell for SniperBot — glassmorphism trading desk aesthetic.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  SimpleGrid,
  Progress,
  Divider,
  Icon,
  Link,
  Switch,
  Collapse,
  IconButton,
  Select,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import {
  FiActivity,
  FiTrendingUp,
  FiTrendingDown,
  FiZap,
  FiShield,
  FiBarChart2,
  FiRefreshCw,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { MdShowChart, MdGridOn } from "react-icons/md";

export const BOT_THEME = {
  bg: "linear-gradient(160deg, #06080f 0%, #0c1222 35%, #0a1628 70%, #060a14 100%)",
  card: "rgba(12, 18, 32, 0.92)",
  cardBorder: "rgba(99, 179, 237, 0.18)",
  cardHover: "rgba(20, 30, 52, 0.95)",
  accent: "#38bdf8",
  accentMuted: "#0ea5e9",
  profit: "#4ade80",
  loss: "#f87171",
  warn: "#fbbf24",
  purple: "#c084fc",
};

const glass = {
  bg: BOT_THEME.card,
  borderWidth: "1px",
  borderColor: BOT_THEME.cardBorder,
  borderRadius: "2xl",
  backdropFilter: "blur(16px)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
};

export function VersionBar({ version, date, note }) {
  if (!version) return null;
  return (
    <Flex
      mb={4}
      py={2}
      px={3}
      align="center"
      justify="space-between"
      flexWrap="wrap"
      gap={2}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="whiteAlpha.100"
      bg="blackAlpha.400"
    >
      <HStack spacing={2} flexWrap="wrap">
        <Badge
          px={2}
          py={0.5}
          borderRadius="md"
          bg="rgba(56, 189, 248, 0.12)"
          color="cyan.200"
          borderWidth="1px"
          borderColor="cyan.700"
          fontSize="xs"
          fontFamily="mono"
        >
          v{version}
        </Badge>
        {date && (
          <Text fontSize="xs" color="gray.500" fontFamily="mono">
            {date}
          </Text>
        )}
        {note && (
          <Text fontSize="xs" color="gray.500">
            · {note}
          </Text>
        )}
      </HStack>
      <Text fontSize="xs" color="gray.600">
        Bump in botVersion.js before deploy
      </Text>
    </Flex>
  );
}

export function DashboardRoot({ children }) {
  return (
    <Box
      w="100%"
      maxW="1440px"
      mx="auto"
      px={{ base: 3, md: 6 }}
      py={{ base: 4, md: 8 }}
      bg={BOT_THEME.bg}
      color="gray.100"
      minH="100vh"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: "absolute",
        top: "-20%",
        right: "-10%",
        w: "50%",
        h: "50%",
        bg: "radial-gradient(circle, rgba(56,189,248,0.08) 0%, transparent 70%)",
        pointerEvents: "none",
      }}
      _after={{
        content: '""',
        position: "absolute",
        bottom: "-15%",
        left: "-5%",
        w: "40%",
        h: "40%",
        bg: "radial-gradient(circle, rgba(168,85,247,0.06) 0%, transparent 70%)",
        pointerEvents: "none",
      }}
    >
      <Box position="relative" zIndex={1}>
        {children}
      </Box>
    </Box>
  );
}

export function HeroHeader({
  chainName,
  dexName,
  isListening,
  killSwitch,
  showDebug,
  onToggleDebug,
  onToggleKill,
  onRefreshBalances,
  account,
}) {
  return (
    <Flex
      direction={{ base: "column", lg: "row" }}
      justify="space-between"
      align={{ base: "stretch", lg: "center" }}
      gap={4}
      mb={6}
    >
      <VStack align="start" spacing={2} flex={1}>
        <HStack spacing={2} flexWrap="wrap">
          <Badge
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(56, 189, 248, 0.15)"
            color="cyan.200"
            borderWidth="1px"
            borderColor="cyan.600"
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            <Icon as={FiZap} mr={1} /> Reckless Command
          </Badge>
          {isListening && (
            <Badge
              px={3}
              py={1}
              borderRadius="full"
              bg="rgba(74, 222, 128, 0.15)"
              color="green.200"
              animation="pulse 2s ease-in-out infinite"
            >
              <Icon as={FiActivity} mr={1} /> Live
            </Badge>
          )}
          {killSwitch && (
            <Badge px={3} py={1} borderRadius="full" bg="red.900" color="red.200">
              Kill switch ON
            </Badge>
          )}
        </HStack>
        <Text
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight="800"
          letterSpacing="-0.02em"
          bgGradient="linear(to-r, cyan.200, blue.300, purple.300)"
          bgClip="text"
        >
          Grid Trading Command Center
        </Text>
        <Text fontSize="sm" color="gray.400" maxW="600px">
          {chainName || "Network"} · {dexName || "DEX"} · Adaptive grid with hybrid recovery & risk controls
        </Text>
      </VStack>

      <HStack spacing={2} flexWrap="wrap" justify={{ base: "flex-start", lg: "flex-end" }}>
        <Button
          as={RouterLink}
          to="/bot-stats"
          size="sm"
          variant="outline"
          colorScheme="cyan"
          leftIcon={<FiBarChart2 />}
        >
          Live stats
        </Button>
        {account && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="cyan"
            leftIcon={<FiRefreshCw />}
            onClick={onRefreshBalances}
          >
            Balances
          </Button>
        )}
        <ControlToggle label="Debug" isChecked={showDebug} onChange={onToggleDebug} />
        <ControlToggle label="Kill" isChecked={killSwitch} onChange={onToggleKill} danger />
      </HStack>
    </Flex>
  );
}

function ControlToggle({ label, isChecked, onChange, danger }) {
  return (
    <Button
      size="sm"
      variant={isChecked ? "solid" : "outline"}
      colorScheme={danger ? "red" : "cyan"}
      onClick={onChange}
      opacity={isChecked ? 1 : 0.7}
    >
      {label}: {isChecked ? "ON" : "OFF"}
    </Button>
  );
}

const OPERATOR_WALLET = "0x7081d3fb56f71868ad55780582bc37bbc536e6d6";

/**
 * Sticky-style command deck under the hero — quick actions, listener, operator options.
 */
export function CommandStrip({
  account,
  isListening,
  killSwitch,
  quickMarketKey,
  marketOptions = [],
  onQuickMarketChange,
  onRefreshPrices,
  onQuickBuy,
  onQuickSell,
  onClearStats,
  onToggleListening,
  useBackendWallet,
  onToggleBackendWallet,
  regimeEngineEnabled,
  onToggleRegimeEngine,
  buyProtectionEnabled,
  onToggleBuyProtection,
}) {
  const isOperator =
    account && String(account).toLowerCase() === OPERATOR_WALLET;
  const walletConnected = !!account;
  const canTrade = walletConnected && quickMarketKey;

  return (
    <Box {...glass} mb={4} p={{ base: 3, md: 4 }}>
      <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={3}>
        <HStack spacing={2} flexWrap="wrap">
          <Icon as={FiZap} color="cyan.300" />
          <Text fontWeight="700" color="white" fontSize="md">
            Command deck
          </Text>
          {!walletConnected && (
            <Badge colorScheme="yellow" fontSize="xs">
              Connect wallet
            </Badge>
          )}
          {killSwitch && (
            <Badge colorScheme="red" fontSize="xs">
              Kill switch ON
            </Badge>
          )}
        </HStack>
        <HStack spacing={3} flexWrap="wrap" fontSize="xs" color="gray.500">
          <HStack spacing={1}>
            <Text>Regime</Text>
            <Switch
              size="sm"
              isChecked={regimeEngineEnabled}
              onChange={(e) => onToggleRegimeEngine?.(e.target.checked)}
              colorScheme="purple"
            />
          </HStack>
          <HStack spacing={1}>
            <Text>Buy protect</Text>
            <Switch
              size="sm"
              isChecked={buyProtectionEnabled}
              onChange={(e) => onToggleBuyProtection?.(e.target.checked)}
              colorScheme="cyan"
            />
          </HStack>
        </HStack>
      </Flex>

      {isOperator && (
        <Box
          mb={3}
          py={2}
          px={3}
          borderRadius="md"
          borderWidth="1px"
          borderColor="orange.600"
          bg="rgba(251, 146, 60, 0.08)"
        >
          <HStack justify="space-between" flexWrap="wrap">
            <Text fontSize="sm" color="orange.200">
              Operator · backend wallet signs trades
            </Text>
            <HStack>
              <Switch
                size="sm"
                isChecked={useBackendWallet}
                onChange={onToggleBackendWallet}
                colorScheme="orange"
              />
              <Text fontSize="xs" color="gray.400">
                {useBackendWallet ? "Backend ON" : "Frontend wallet"}
              </Text>
            </HStack>
          </HStack>
        </Box>
      )}

      <Flex gap={3} flexWrap="wrap" align="flex-end">
        {marketOptions.length > 0 && (
          <Box minW={{ base: "100%", sm: "200px" }} flex={{ base: "1 1 100%", sm: "0 1 220px" }}>
            <Text fontSize="xs" color="gray.500" mb={1}>
              Quick trade market
            </Text>
            <Select
              size="sm"
              value={quickMarketKey || ""}
              onChange={(e) => onQuickMarketChange?.(e.target.value)}
              bg="gray.800"
              borderColor="gray.600"
              isDisabled={!walletConnected}
            >
              {marketOptions.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Box>
        )}

        <HStack spacing={2} flexWrap="wrap" flex={1} justify={{ base: "flex-start", md: "flex-end" }}>
          <StyledActionButton
            size="sm"
            variant="outline"
            onClick={onRefreshPrices}
            isDisabled={!walletConnected}
            leftIcon={<FiRefreshCw />}
          >
            Refresh prices
          </StyledActionButton>
          <StyledActionButton
            size="sm"
            colorScheme="green"
            onClick={onQuickBuy}
            isDisabled={!canTrade || killSwitch}
          >
            Quick buy
          </StyledActionButton>
          <StyledActionButton
            size="sm"
            colorScheme="red"
            onClick={onQuickSell}
            isDisabled={!canTrade || killSwitch}
          >
            Quick sell
          </StyledActionButton>
          <StyledActionButton
            size="sm"
            variant="outline"
            onClick={onClearStats}
            isDisabled={!walletConnected}
          >
            Clear stats
          </StyledActionButton>
          <StyledActionButton
            size="sm"
            colorScheme={isListening ? "red" : "purple"}
            onClick={onToggleListening}
            isDisabled={!walletConnected}
          >
            {isListening ? "Stop listeners" : "Start listeners"}
          </StyledActionButton>
        </HStack>
      </Flex>
    </Box>
  );
}

export function LiveTicker({ message, active }) {
  if (!active && !message) return null;
  return (
    <Box
      mb={6}
      py={3}
      px={4}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={active ? "rgba(74, 222, 128, 0.4)" : "gray.600"}
      bg={active ? "rgba(34, 197, 94, 0.12)" : "rgba(30, 41, 59, 0.6)"}
      textAlign="center"
    >
      <HStack justify="center" spacing={2}>
        {active && (
          <Box w={2} h={2} borderRadius="full" bg="green.400" boxShadow="0 0 8px #4ade80" />
        )}
        <Text fontSize="sm" fontWeight="semibold" color={active ? "green.200" : "gray.400"}>
          {message || "Listeners idle"}
        </Text>
      </HStack>
    </Box>
  );
}

export function Panel({ title, subtitle, icon, accent = "cyan", children, rightAction }) {
  const accentMap = {
    cyan: { border: "cyan.700", icon: "cyan.300", badge: "cyan" },
    purple: { border: "purple.700", icon: "purple.300", badge: "purple" },
    orange: { border: "orange.700", icon: "orange.300", badge: "orange" },
    red: { border: "red.800", icon: "red.300", badge: "red" },
  };
  const a = accentMap[accent] || accentMap.cyan;
  return (
    <Box {...glass} borderColor={a.border} p={{ base: 4, md: 5 }} mb={6}>
      <Flex justify="space-between" align="start" mb={4} flexWrap="wrap" gap={2}>
        <HStack spacing={3}>
          {icon && (
            <Flex
              w={10}
              h={10}
              align="center"
              justify="center"
              borderRadius="lg"
              bg={`${a.badge}.900`}
              color={`${a.badge}.200`}
            >
              <Icon as={icon} boxSize={5} />
            </Flex>
          )}
          <VStack align="start" spacing={0}>
            <Text fontWeight="700" fontSize="lg" color="white">
              {title}
            </Text>
            {subtitle && (
              <Text fontSize="xs" color="gray.500">
                {subtitle}
              </Text>
            )}
          </VStack>
        </HStack>
        {rightAction}
      </Flex>
      {children}
    </Box>
  );
}

export function BalanceStrip({ items }) {
  return (
    <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3} mb={6}>
      {items.map((item) => (
        <Box
          key={item.label}
          p={4}
          borderRadius="xl"
          bg="rgba(15, 23, 42, 0.8)"
          borderWidth="1px"
          borderColor="rgba(148, 163, 184, 0.1)"
          _hover={{ borderColor: "cyan.700", transform: "translateY(-1px)" }}
          transition="all 0.2s"
        >
          <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>
            {item.label}
          </Text>
          <Text fontSize="lg" fontWeight="700" color={item.accent || "white"} fontFamily="mono">
            {item.value}
          </Text>
          {item.sub && (
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              {item.sub}
            </Text>
          )}
        </Box>
      ))}
    </SimpleGrid>
  );
}

export function MarketsSectionHeader({ activeCount = 0, inactiveCount = 0 }) {
  return (
    <HStack mb={4} spacing={3} justify="space-between" flexWrap="wrap">
      <HStack spacing={3}>
        <Icon as={MdGridOn} color="cyan.300" boxSize={6} />
        <VStack align="start" spacing={0}>
          <Text fontSize="xl" fontWeight="700" color="white">
            Markets
          </Text>
          <Text fontSize="sm" color="gray.500">
            {activeCount} active · {inactiveCount} inactive (collapsed)
          </Text>
        </VStack>
      </HStack>
    </HStack>
  );
}

export function MarketCard({
  displayTitle,
  enabled,
  onToggle,
  price,
  buyReady,
  sellReady,
  recoveryMode,
  distToBuy,
  distToSell,
  children,
  footer,
}) {
  const [expanded, setExpanded] = useState(enabled);

  useEffect(() => {
    if (enabled) {
      setExpanded(true);
    } else {
      setExpanded(false);
    }
  }, [enabled]);

  const showBody = enabled || expanded;

  const borderGlow = recoveryMode
    ? "rgba(192, 132, 252, 0.5)"
    : enabled
    ? "rgba(56, 189, 248, 0.35)"
    : "rgba(71, 85, 105, 0.4)";

  return (
    <Box
      {...glass}
      borderColor={borderGlow}
      opacity={enabled ? 1 : 0.55}
      transition="all 0.25s ease"
      _hover={{ borderColor: "cyan.500", boxShadow: "0 12px 40px rgba(0,0,0,0.5)" }}
      w="100%"
      maxW="100%"
    >
      <Flex
        px={5}
        py={4}
        borderBottomWidth={showBody ? "1px" : 0}
        borderColor="rgba(148, 163, 184, 0.1)"
        justify="space-between"
        align="center"
        flexWrap="wrap"
        gap={3}
        bg="rgba(0,0,0,0.2)"
        borderTopRadius="2xl"
        borderBottomRadius={showBody ? 0 : "2xl"}
        cursor={!enabled ? "pointer" : "default"}
        onClick={() => {
          if (!enabled) setExpanded((v) => !v);
        }}
      >
        <HStack spacing={3}>
          {!enabled && (
            <IconButton
              aria-label={expanded ? "Collapse market" : "Expand market"}
              icon={<Icon as={expanded ? FiChevronUp : FiChevronDown} />}
              size="xs"
              variant="ghost"
              colorScheme="cyan"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            />
          )}
          <Icon as={MdShowChart} color="cyan.300" boxSize={5} />
          <VStack align="start" spacing={0}>
            <HStack spacing={2}>
              <Text fontWeight="700" fontSize="lg" color="white">
                {displayTitle}
              </Text>
              {!enabled && (
                <Badge colorScheme="gray" fontSize="2xs">
                  Inactive
                </Badge>
              )}
            </HStack>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.400" fontFamily="mono">
                ${price}
              </Text>
              {recoveryMode && (
                <Badge colorScheme="purple" fontSize="2xs">
                  Recovery grid
                </Badge>
              )}
            </HStack>
          </VStack>
        </HStack>
        <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
          <StatusChip ready={buyReady} label="BUY" readyColor="green" />
          <StatusChip ready={sellReady} label="SELL" readyColor="red" />
          <Button
            size="sm"
            variant={enabled ? "solid" : "outline"}
            colorScheme={enabled ? "cyan" : "gray"}
            onClick={onToggle}
          >
            {enabled ? "ON" : "OFF"}
          </Button>
        </HStack>
      </Flex>

      <Collapse in={showBody} animateOpacity unmountOnExit={false}>
        {(distToBuy != null || distToSell != null) && (
          <SimpleGrid columns={2} spacing={0} borderBottomWidth="1px" borderColor="rgba(148,163,184,0.08)">
            <TargetDistance label="To buy zone" value={distToBuy} invert ready={buyReady} />
            <TargetDistance label="To sell zone" value={distToSell} ready={sellReady} />
          </SimpleGrid>
        )}

        <Box p={5}>{children}</Box>
        {footer && (
          <Box px={5} pb={5} pt={0}>
            {footer}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

function StatusChip({ ready, label, readyColor }) {
  return (
    <Badge
      px={2}
      py={1}
      borderRadius="md"
      bg={ready ? `${readyColor}.900` : "gray.800"}
      color={ready ? `${readyColor}.200` : "gray.500"}
      borderWidth="1px"
      borderColor={ready ? `${readyColor}.600` : "gray.700"}
    >
      {label} {ready ? "✓" : "—"}
    </Badge>
  );
}

function TargetDistance({ label, value, invert, ready }) {
  const pct = value != null ? Math.abs(Number(value)) : 0;
  const clamped = Math.min(100, pct);
  const color = ready ? "green" : invert ? (value < 0 ? "green" : "orange") : value > 0 ? "green" : "orange";
  return (
    <Box p={3} borderRightWidth={label.includes("sell") ? 0 : "1px"} borderColor="rgba(148,163,184,0.08)">
      <HStack justify="space-between" mb={1}>
        <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
          {label}
        </Text>
        <Text fontSize="xs" fontFamily="mono" color={`${color}.300`}>
          {value != null ? `${Number(value).toFixed(2)}%` : "—"}
        </Text>
      </HStack>
      <Progress
        value={clamped}
        size="xs"
        borderRadius="full"
        colorScheme={color}
        bg="gray.800"
      />
    </Box>
  );
}

export function MetricRow({ items }) {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3} mb={4}>
      {items.map((m) => (
        <Box key={m.label} p={3} borderRadius="lg" bg="rgba(0,0,0,0.25)" borderWidth="1px" borderColor="gray.700">
          <Text fontSize="2xs" color="gray.500" mb={1}>
            {m.label}
          </Text>
          <Text fontSize="md" fontWeight="600" color={m.color || "gray.200"} fontFamily="mono">
            {m.value}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  );
}

export function InsightBox({ title, variant = "info", children }) {
  const styles = {
    info: { border: "cyan.800", bg: "rgba(6, 182, 212, 0.08)", title: "cyan.200" },
    warn: { border: "orange.800", bg: "rgba(251, 191, 36, 0.08)", title: "orange.200" },
    danger: { border: "red.800", bg: "rgba(248, 113, 113, 0.08)", title: "red.200" },
    success: { border: "green.800", bg: "rgba(74, 222, 128, 0.08)", title: "green.200" },
    debug: { border: "purple.800", bg: "rgba(192, 132, 252, 0.08)", title: "purple.200" },
  };
  const s = styles[variant] || styles.info;
  return (
    <Box mt={3} p={3} borderRadius="lg" borderWidth="1px" borderColor={s.border} bg={s.bg}>
      {title && (
        <Text fontSize="sm" fontWeight="600" color={s.title} mb={2}>
          {title}
        </Text>
      )}
      {children}
    </Box>
  );
}

export function PnLHero({
  totalPnL,
  totalPnLPct,
  realized,
  unrealized,
  totalTrades,
  positive,
  children,
}) {
  return (
    <Box
      {...glass}
      borderColor={positive ? "rgba(74, 222, 128, 0.35)" : "rgba(248, 113, 113, 0.35)"}
      p={6}
      mb={6}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        right={0}
        w="200px"
        h="200px"
        bg={
          positive
            ? "radial-gradient(circle, rgba(74,222,128,0.12) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(248,113,113,0.12) 0%, transparent 70%)"
        }
        pointerEvents="none"
      />
      <Flex justify="space-between" align="start" flexWrap="wrap" gap={4} mb={4}>
        <VStack align="start" spacing={1}>
          <HStack>
            <Icon as={positive ? FiTrendingUp : FiTrendingDown} color={positive ? "green.300" : "red.300"} />
            <Text fontSize="sm" color="gray.400" textTransform="uppercase" letterSpacing="wider">
              Portfolio performance
            </Text>
          </HStack>
          <Text
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="800"
            fontFamily="mono"
            color={positive ? "green.300" : "red.300"}
          >
            {totalPnL >= 0 ? "+" : ""}
            {typeof totalPnL === "number" ? totalPnL.toFixed(2) : totalPnL} USDC
          </Text>
          <Text fontSize="md" color="gray.400">
            {typeof totalPnLPct === "number" ? `${totalPnLPct >= 0 ? "+" : ""}${totalPnLPct.toFixed(2)}%` : "—"} ROI
            · {totalTrades} trades
          </Text>
        </VStack>
        <SimpleGrid columns={2} spacing={4} minW="200px">
          <MiniStat label="Realized" value={realized} positive={realized >= 0} />
          <MiniStat label="Unrealized" value={unrealized} positive={unrealized >= 0} />
        </SimpleGrid>
      </Flex>
      {children}
    </Box>
  );
}

function MiniStat({ label, value, positive }) {
  return (
    <Box p={3} borderRadius="lg" bg="rgba(0,0,0,0.3)">
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Text fontSize="lg" fontWeight="700" fontFamily="mono" color={positive ? "green.300" : "red.300"}>
        {typeof value === "number" ? `${value >= 0 ? "+" : ""}${value.toFixed(2)}` : value}
      </Text>
    </Box>
  );
}

export function ActionRow({ children }) {
  return (
    <HStack spacing={2} mt={4} flexWrap="wrap">
      {children}
    </HStack>
  );
}

export const fieldInputProps = {
  bg: "rgba(15, 23, 42, 0.92)",
  borderColor: "rgba(148, 163, 184, 0.22)",
  color: "gray.100",
  _hover: { borderColor: "cyan.600" },
  _focus: {
    borderColor: "cyan.400",
    boxShadow: "0 0 0 1px rgba(56, 189, 248, 0.35)",
  },
};

export function FormField({ label, hint, children }) {
  return (
    <Box>
      {label && (
        <Text fontSize="sm" color="gray.300" mb={1.5} fontWeight="500">
          {label}
        </Text>
      )}
      {children}
      {hint && (
        <Text fontSize="xs" color="gray.500" mt={1.5} lineHeight="short">
          {hint}
        </Text>
      )}
    </Box>
  );
}

export function SettingsBlock({
  title,
  description,
  accent = "cyan",
  enabled,
  onToggle,
  icon,
  children,
}) {
  const map = {
    cyan: {
      border: "rgba(34, 211, 238, 0.3)",
      bg: "rgba(6, 182, 212, 0.07)",
      title: "cyan.200",
      scheme: "cyan",
    },
    purple: {
      border: "rgba(192, 132, 252, 0.35)",
      bg: "rgba(168, 85, 247, 0.08)",
      title: "purple.200",
      scheme: "purple",
    },
    red: {
      border: "rgba(248, 113, 113, 0.35)",
      bg: "rgba(248, 113, 113, 0.07)",
      title: "red.200",
      scheme: "red",
    },
    orange: {
      border: "rgba(251, 146, 60, 0.35)",
      bg: "rgba(251, 146, 60, 0.07)",
      title: "orange.200",
      scheme: "orange",
    },
  };
  const s = map[accent] || map.cyan;
  return (
    <Box
      mt={4}
      p={{ base: 3, md: 4 }}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={s.border}
      bg={s.bg}
    >
      <Flex justify="space-between" align="start" mb={children ? 4 : 0} flexWrap="wrap" gap={3}>
        <HStack align="start" spacing={3} flex={1} minW="240px">
          {icon && <Icon as={icon} color={s.title} boxSize={5} mt={0.5} />}
          <VStack align="start" spacing={1}>
            <Text fontSize="md" fontWeight="700" color={s.title}>
              {title}
            </Text>
            {description && (
              <Text fontSize="xs" color="gray.500" maxW="720px" lineHeight="tall">
                {description}
              </Text>
            )}
          </VStack>
        </HStack>
        {onToggle != null && (
          <HStack spacing={2} flexShrink={0}>
            <Text fontSize="sm" color="gray.400">
              {enabled ? "On" : "Off"}
            </Text>
            <Switch
              isChecked={enabled}
              onChange={onToggle}
              colorScheme={s.scheme}
            />
          </HStack>
        )}
      </Flex>
      {children}
    </Box>
  );
}

export function StyledActionButton({ children, colorScheme = "cyan", variant = "solid", ...props }) {
  return (
    <Button
      size="sm"
      colorScheme={colorScheme}
      variant={variant}
      borderRadius="lg"
      fontWeight="600"
      letterSpacing="0.01em"
      _hover={{ transform: "translateY(-1px)", boxShadow: "md" }}
      transition="all 0.15s ease"
      {...props}
    >
      {children}
    </Button>
  );
}

export function GlobalStyles() {
  return (
    <style>{`
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.65; }
      }
    `}</style>
  );
}

export function ArbiscanLink({ txHash }) {
  if (!txHash) return null;
  return (
    <Link href={`https://arbiscan.io/tx/${txHash}`} isExternal color="cyan.400" fontSize="xs">
      <HStack spacing={1}>
        <Text fontFamily="mono">{txHash.slice(0, 10)}…</Text>
        <Icon as={FiExternalLink} />
      </HStack>
    </Link>
  );
}
